import {
	AfterContentInit,
	ComponentRef,
	ContentChildren,
	Directive,
	DoCheck,
	ElementRef,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	QueryList,
	Renderer2,
	ViewContainerRef
} from '@angular/core';
import {Subscription} from 'rxjs';
import _ from 'lodash';
import {TrendsComparisonService} from '../../services/trendsComparisonService';
import {NotificationService} from '../../services/notificationService';
import {DICTIONARY} from '../../dictionary';
import {TrendsComparisonCardDirective} from "./trendsComparisonCard/trends-comparison-card.directive";

@Directive({
	selector: '[appTrendsComparison]'
})
export class TrendsComparisonDirective implements OnInit, AfterContentInit, DoCheck, OnDestroy {
	@Input() appTrendsComparison: {host: any, componentClass: any};

	@ContentChildren(TrendsComparisonCardDirective, {descendants: true})
	cardsQuery: QueryList<TrendsComparisonCardDirective>;

	subscriptions: Subscription[] = [];
	ghostRef: ComponentRef<any> = null;
	comparisonParams: any = null;
	// CC: runToken is incremented on every new comparison run — used to discard stale callbacks from a previous run
	runToken = 0;
	// CC: pushToken is incremented on every schedulePushCardPayloads call — ensures only the latest scheduled push executes
	pushToken = 0;
	private allFinished = false;
	comparisonRunOverlay: HTMLElement = null;
	savedBodyOverflow: string = null;
	restoreHostHooks: (() => void) | null = null;
	pendingLoadingReset = false;
	private firstBatchShown = false;
	cards: TrendsComparisonCardDirective[] = [];
	private lastCardState = new WeakMap<TrendsComparisonCardDirective, string>();
	dic = DICTIONARY;
	// CC: chart options captured after each host/ghost prepareData call — used to build comparison payloads
	private rawCurrentOptions: {[key: string]: any} = {};
	private rawComparedOptions: {[key: string]: any} = {};
	// CC: last patch/payload actually pushed to each card — used to skip redundant updates
	private lastAppliedPatchOptions: {[key: string]: any} = {};
	private lastAppliedComparedOptions: {[key: string]: any} = {};
	// CC: series values captured from intercepted updateSeries calls (fast-path in prepare*Graph methods that skip full re-init)
	private pendingSeriesUpdates: {[key: string]: any} = {};
	// CC: original updateSeries functions saved before interception so they can be called directly for left-chart series-only updates
	private originalUpdateSeriesFns: Map<string, Function> = new Map();
	private anyDataProcessStarted = false;
	// CC: guards against overlapping pushCardPayloads executions; pendingDataProcess queues one retry if a push arrives while busy
	private dataProcessInProgress = false;
	private pendingDataProcess = false;
	private getTrendsDataInProcessDescriptor: PropertyDescriptor = null;

	constructor(private el: ElementRef,
				private renderer: Renderer2,
				private viewContainerRef: ViewContainerRef,
				private ngZone: NgZone,
				public comparisonService: TrendsComparisonService,
				private ns: NotificationService) {
	}

	ngOnInit() {
		// CC: comparisonRequested$ is emitted by TrendsComparisonService.activateComparison() with the two date ranges
		this.subscriptions.push(
			this.comparisonService.comparisonRequested$.subscribe(params => {
				this.runComparison(params);
			})
		);

		// CC: comparisonDeactivated$ tears down the ghost and restores the host to normal (default period, fresh fetch)
		this.subscriptions.push(
			this.comparisonService.comparisonDeactivated$.subscribe(() => {
				this.teardown();
				// restore host to the default period after exiting comparison mode (skip if host is being destroyed)
				if (!this.appTrendsComparison?.host?.abortLoadingFlag) {
					this.appTrendsComparison.host.period = this.dic.CONSTANTS.trendsPeriod.last3Days;
					this.appTrendsComparison.host.getTrendsData(false);
				}
			})
		);

		// CC: comparisonBatchAbort$ stops in-flight fetches without fully exiting comparison mode.
		// if no data arrived yet, falls back to a full teardown + host restore.
		this.subscriptions.push(
			this.comparisonService.comparisonBatchAbort$.subscribe(() => {
				this.runToken++;
				const host = this.appTrendsComparison?.host;
				if (this.ghostRef?.instance) {
					this.ghostRef.instance.abortLoadingFlag = true;
				}

				if (!this.anyDataProcessStarted) {
					this.teardown();
					if (host) {
						host.abortLoadingFlag = false;
						host.period = this.dic.CONSTANTS.trendsPeriod.last3Days;
						host.getTrendsData(false);
					}
					return;
				}

				if (host) {
					host.abortLoadingFlag = true;
				}
				this.cards.forEach(card => card.setLoading(false));
				this.removeComparisonRunOverlay();
			})
		);
	}

	ngAfterContentInit() {
		this.syncCards();
		this.subscriptions.push(
			this.cardsQuery.changes.subscribe(() => {
				this.syncCards();
				if (this.comparisonService.comparisonActive) {
					this.cards.forEach(card => card.setActive(true));
					this.schedulePushCardPayloads();
				}
			})
		);
	}

	ngDoCheck() {
		if (!this.comparisonService.comparisonActive || !this.cards?.length) {
			return;
		}

		let changed = false;
		this.cards.forEach(card => {
			const state = `${card.comparisonKey || ''}|${card.chartRefKey || ''}`;
			if (this.lastCardState.get(card) !== state) {
				this.lastCardState.set(card, state);
				changed = true;
			}
		});

		if (changed) {
			this.schedulePushCardPayloads();
		}
	}

	ngOnDestroy() {
		this.teardown();
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	runComparison(params) {
		this.comparisonParams = params;
		// abort any in-flight host/ghost requests before teardown so stale callbacks don't bleed into the new run
		const hostRef = this.appTrendsComparison?.host;
		if (hostRef) {
			hostRef.abortLoadingFlag = true;
		}
		if (this.ghostRef?.instance) {
			this.ghostRef.instance.abortLoadingFlag = true;
		}
		this.teardown(false);
		this.runToken++;
		this.firstBatchShown = false;
		this.anyDataProcessStarted = false;
		this.dataProcessInProgress = false;
		this.pendingDataProcess = false;
		this.rawCurrentOptions = {};
		this.rawComparedOptions = {};
		this.lastAppliedPatchOptions = {};
		this.lastAppliedComparedOptions = {};
		this.pendingSeriesUpdates = {};
		this.comparisonService.comparisonLoading = true;
		this.comparisonService.comparisonError = false;
		this.comparisonService.comparisonProgressPercent = 0;
		this.showComparisonRunOverlay();
		this.setCardsActive(true);
		this.setCardsLoading(true);
		this.createGhostAndFetch(params);
	}

	registerCard(card: TrendsComparisonCardDirective) {
		// Kept for compatibility if a page manually calls it. The primary mechanism is @ContentChildren.
		if (this.cards.indexOf(card) === -1) {
			this.cards.push(card);
		}
	}

	unregisterCard(card: TrendsComparisonCardDirective) {
		this.cards = this.cards.filter(c => c !== card);
	}

	private syncCards() {
		this.cards = this.cardsQuery ? this.cardsQuery.toArray().filter(card => !!card?.comparisonKey) : [];
	}

	private setCardsActive(active: boolean) {
		this.syncCards();
		if (!active) {
			this.lastCardState = new WeakMap<TrendsComparisonCardDirective, string>();
		}
		this.cards.forEach(card => card.setActive(active));
	}

	private setCardsLoading(loading: boolean) {
		this.syncCards();
		this.cards.forEach(card => card.setLoading(loading));
	}

	private showComparisonRunOverlay() {
		const container = this.el.nativeElement;
		container.style.position = 'relative';
		this.renderer.addClass(container, 'comparison-mode');

		// #main is the actual scroll container (app-content-container with overflow:auto)
		const mainEl = document.getElementById('main');
		this.savedBodyOverflow = mainEl ? mainEl.style.overflow : null;
		if (mainEl) {
			mainEl.style.overflow = 'hidden';
		}

		const overlay = document.createElement('div');
		overlay.className = 'comparison-run-overlay';
		overlay.innerHTML = `
			<div style="position:absolute; top:calc(50vh - 200px - 40px); left:50%; transform:translate(-50%,0); font-size:4.3rem; opacity:0.5; align-items:center;"
			     class="animated flxClmn vSpace2 infinite">
				<div class="animated bounce infinite">
					<i class="far fa-chart-line comparison-run-icon" style="filter:contrast(1.5) drop-shadow(2px -1px 1px lightgrey);"></i>
				</div>
				<span style="position:relative; font-size:1.3rem;" class="loading-dots sss">Comparing periods</span>
			</div>`;
		container.appendChild(overlay);
		this.comparisonRunOverlay = overlay;
	}

	private removeComparisonRunOverlay() {
		if (this.comparisonRunOverlay) {
			this.comparisonRunOverlay.remove();
			this.comparisonRunOverlay = null;
		}
		this.el.nativeElement.style.position = '';

		if (this.savedBodyOverflow !== null) {
			const mainEl = document.getElementById('main');
			if (mainEl) {
				mainEl.style.overflow = this.savedBodyOverflow;
			}
			this.savedBodyOverflow = null;
		}

		if (this.pendingLoadingReset) {
			this.pendingLoadingReset = false;
			this.comparisonService.comparisonLoading = false;
		}
	}

	private createGhostAndFetch(params) {
		if (!this.appTrendsComparison?.componentClass) {
			this.ns.showWarnMessage('Comparison component class not provided');
			this.comparisonService.onGhostDataError();
			return;
		}

		try {
			this.destroyGhost();
			// CC: isGhostMode flag is read by the component's ngOnInit to skip normal initialization (data fetch, user load, etc.)
			this.comparisonService.isGhostMode = true;
			this.ghostRef = this.viewContainerRef.createComponent(this.appTrendsComparison.componentClass);
			this.comparisonService.ghostInstance = this.ghostRef.instance;

			const ghost = this.ghostRef.instance;
			const ghostElement = this.ghostRef.location.nativeElement;
			// CC: remove the ghost's DOM node immediately — it must never render visually
			if (ghostElement.parentNode) {
				ghostElement.parentNode.removeChild(ghostElement);
			}

			// CC: chart ViewChild refs on the ghost are noop proxies — the ghost never renders charts, only computes options
			const noopProxy = new Proxy({}, {get: () => () => {}});
			this.stubChartRefs(ghost, noopProxy);

			ghost.userInfo = this.appTrendsComparison.host?.userInfo;
			ghost.period = {display: 'Range', value: 'range'};
			ghost.range = params.comparedRange;

			const runToken = this.runToken;
			let hostBatchCount = 0;
			let ghostBatchCount = 0;
			let hostFinished = false;
			let ghostFinished = false;

			const updateProgress = () => {
				if (this.runToken !== runToken) {
					return;
				}
				const host = this.appTrendsComparison.host;
				const ghostInst = this.ghostRef?.instance;
				const hostPct = host?.totalEmails > 0 ? (host.emails?.length || 0) / host.totalEmails : (hostBatchCount ? 1 : 0);
				const ghostPct = ghostInst?.totalEmails > 0 ? (ghostInst.emails?.length || 0) / ghostInst.totalEmails : (ghostBatchCount ? 1 : 0);
				this.comparisonService.comparisonProgressPercent = Math.round((hostPct + ghostPct) / 2 * 100);
			};

			const afterAnyBatch = () => {
				if (this.runToken !== runToken) {
					return;
				}
				updateProgress();
				this.schedulePushCardPayloads();
				this.comparisonService.comparisonDataReady$.next(null);
			};

			const host = this.appTrendsComparison.host;
			if (host) {
				const originalPrepareData = host.prepareData;
				const originalHostInit = host.initTrendsProcessParams;

				// CC: restoreHostHooks is called once after the host signals completion (initTrendsProcessParams),
				// putting the original methods back so normal operation resumes after the comparison run
				this.restoreHostHooks = () => {
					host.prepareData = originalPrepareData;
					host.initTrendsProcessParams = originalHostInit;
					this.restoreHostHooks = null;
				};

				// CC: wrapped prepareData captures the host's chart options into rawCurrentOptions after each batch
				host.prepareData = (isReload) => {
					// gate before DATA PROCESS starts — if token changed, skip entirely
					if (this.runToken !== runToken) {
						return;
					}
					this.anyDataProcessStarted = true;
					// discard any pendingSeriesUpdates left by the previous batch's Angular CD cycle;
					// only synchronous updateSeries calls captured during originalPrepareData are valid
					Object.keys(this.pendingSeriesUpdates).forEach(k => {
						delete this.pendingSeriesUpdates[k];
					});
					if (originalPrepareData) {
						originalPrepareData.call(host, isReload);
					}
					// capture rawCurrentOptions after originalPrepareData; pendingSeriesUpdates holds
					// series values from updateSeries fast-paths (early-return in prepare*Graph methods)
					this.syncCards();
					this.cards.forEach(card => {
						const k = card.comparisonKey;
						if (!k || !host[k]) {
							return;
						}
						this.rawCurrentOptions[k] = _.cloneDeep(host[k]);
						if (this.pendingSeriesUpdates[k] !== undefined) {
							this.rawCurrentOptions[k].series = this.pendingSeriesUpdates[k];
							delete this.pendingSeriesUpdates[k];
						}
						else {
							// full re-init path — Angular will re-render the chart's bound properties,
							// resetting any previously patched options. Invalidate so patch is always re-applied.
							delete this.lastAppliedPatchOptions[k];
						}
					});
					// supplement card-based capture with host-wide scan to handle dynamic-key cards
					// (e.g. topMailboxesChartOptions ↔ topExecutiveMailboxesChartOptions switch).
					// Angular may not have propagated the new card key yet at capture time.
					Object.keys(host).forEach(k => {
						if (k.endsWith('ChartOptions') && host[k]?.series !== undefined && !this.rawCurrentOptions[k]) {
							this.rawCurrentOptions[k] = _.cloneDeep(host[k]);
						}
					});
					hostBatchCount++;
					afterAnyBatch();
				};

				// CC: initTrendsProcessParams is used as a "host finished all processing" signal —
				// the component calls it at the end of its final prepareData to let the directive know
				host.initTrendsProcessParams = () => {
					if (originalHostInit) {
						originalHostInit.call(host);
					}
					hostFinished = true;
					if (this.restoreHostHooks) {
						this.restoreHostHooks();
					}
					if (this.runToken === runToken) {
						this.schedulePushCardPayloads();
						if (ghostFinished) {
							this.finishLoadingWhenSafe();
						}
					}
				};

				host.period = {display: 'Range', value: 'range'};
				host.range = params.currentRange;
				host.showTrendsCustomPeriod = false;
				host.abortLoadingFlag = false;
				this.interceptGetTrendsDataInProcess(host);
				host.getTrendsData(false);
			}

			const originalGhostPrepareData = ghost.prepareData;
			const originalGhostInit = ghost.initTrendsProcessParams;

			// CC: wrapped ghost prepareData captures chart options into rawComparedOptions after each batch
			ghost.prepareData = (isReload) => {
				// gate before DATA PROCESS starts
				if (this.runToken !== runToken) {
					return;
				}
				this.anyDataProcessStarted = true;
				if (originalGhostPrepareData) {
					originalGhostPrepareData.call(ghost, isReload);
				}
				ghostBatchCount++;
				if (ghost.trendsData) {
					this.captureGhostChartOptions(ghost);
				}
				afterAnyBatch();
			};

			// CC: same "finished" signal as the host — ghost restores its own hooks and triggers final payload push
			ghost.initTrendsProcessParams = () => {
				if (this.runToken !== runToken) {
					return;
				}
				if (originalGhostInit) {
					originalGhostInit.call(ghost);
				}
				ghostFinished = true;
				ghost.prepareData = originalGhostPrepareData;
				ghost.initTrendsProcessParams = originalGhostInit;
				this.captureGhostChartOptions(ghost);
				this.schedulePushCardPayloads();
				if (hostFinished) {
					this.finishLoadingWhenSafe();
				}
			};

			ghost.abortLoadingFlag = false;
			ghost.activeSpecificUsers = this.appTrendsComparison.host?.activeSpecificUsers;
			ghost.getTrendsData(false);
		}
		catch (err) {
			console.error('Ghost component creation failed', err);
			this.ns.showWarnMessage('Failed to initialize comparison');
			this.comparisonService.onGhostDataError();
			this.teardown();
		}
	}

	private finishLoadingWhenSafe() {
		this.allFinished = true;
		this.setCardsLoading(false);
		if (this.comparisonRunOverlay) {
			this.pendingLoadingReset = true;
			setTimeout(() => {
				this.removeComparisonRunOverlay();
			}, 700);
		}
		else {
			this.comparisonService.comparisonLoading = false;
		}
	}

	private captureGhostChartOptions(ghost) {
		const chartOptionsMap = {};
		// auto-derive keys from any ghost property ending with 'ChartOptions' that has a series property (even empty)
		Object.keys(ghost).forEach(key => {
			if (key.endsWith('ChartOptions') && ghost[key]?.series !== undefined) {
				chartOptionsMap[key] = _.cloneDeep(ghost[key]);
				this.rawComparedOptions[key] = _.cloneDeep(ghost[key]);
			}
		});
		this.comparisonService.onGhostDataReady(chartOptionsMap);
		return chartOptionsMap;
	}

	private stubChartRefs(ghost, noopProxy) {
		// stub known chart ref patterns - properties that end with 'Chart' and are ViewChild refs
		const proto = Object.getPrototypeOf(ghost);
		const allKeys = [...Object.keys(ghost), ...Object.getOwnPropertyNames(proto)];

		allKeys.forEach(key => {
			if (key.toLowerCase().includes('chart') && !key.toLowerCase().includes('options') && !key.toLowerCase().includes('view')) {
				ghost[key] = noopProxy;
			}
		});
	}

	private schedulePushCardPayloads() {
		if (this.dataProcessInProgress) {
			// a DATA PROCESS is running — queue next run instead of overlapping
			this.pendingDataProcess = true;
			return;
		}
		const token = ++this.pushToken;
		this.ngZone.runOutsideAngular(() => {
			// CC: two-stage scheduling (setTimeout → requestAnimationFrame) lets Angular process *ngIf toggles
			// and ng-apexcharts process updateSeries before we read chart options and push payloads
			setTimeout(() => {
				requestAnimationFrame(() => {
					if (token !== this.pushToken || !this.comparisonService.comparisonActive) {
						return;
					}
					this.ngZone.run(() => this.pushCardPayloads());
				});
			});
		});
	}

	// CC: pushCardPayloads builds and applies comparison payloads for every registered card.
	// Called after each host/ghost batch — may run multiple times as data arrives incrementally.
	private pushCardPayloads() {
		this.dataProcessInProgress = true;
		try {
			const host = this.appTrendsComparison?.host;
			if (!host) {
				return;
			}

			this.syncCards();
			let payloadsBuilt = 0;

			this.cards.forEach(card => {
				const key = card.comparisonKey;
				if (!key) {
					return;
				}

				// intercept updateSeries on chart refs lazily, once they're available in DOM
				this.interceptUpdateSeriesForCard(card, host);

				const rawCurrent = this.rawCurrentOptions[key];
				// CC: rawCompared may be undefined when ghost had null chart options (no data) — handle separately
				let rawCompared = this.rawComparedOptions[key];

				if (!rawCurrent) {
					card.setLoading(!this.allFinished);
					card.setBothEmpty(false);
					if (this.allFinished) {
						card.clearPayload();
					}
					return;
				}

				// ghost had no data for this key (null chart options → not captured) — substitute empty series
				if (rawCompared === undefined && this.allFinished) {
					rawCompared = {series: []};
				}

				if (!rawCompared) {
					card.setLoading(true);
					card.setBothEmpty(false);
					return;
				}

				const payloadInfo = this.buildComparisonPayload(key, rawCurrent, rawCompared, card);

				if (!payloadInfo) {
					card.setLoading(!this.allFinished);
					card.setBothEmpty(false);
					if (this.allFinished) {
						card.clearPayload();
					}
					return;
				}

				card.setBothEmpty(false);

				// step 4 — smart update for left chart: skip / ORIGINAL UPDATESERIES / updateOptions
				const prevPatch = this.lastAppliedPatchOptions[key];
				const newPatch = payloadInfo.currentPatchOptions;

				if (!prevPatch || !_.isEqual(prevPatch, newPatch)) {
					const prevPatchNoSeries = _.omit(prevPatch || {}, ['series']);
					const newPatchNoSeries = _.omit(newPatch, ['series']);
					let patchWasApplied = false;
					if (prevPatch && _.isEqual(prevPatchNoSeries, newPatchNoSeries)) {
						// series only changed — use ORIGINAL UPDATESERIES (faster, no full re-render)
						const originalFn = this.originalUpdateSeriesFns.get(card.chartRefKey);
						if (originalFn && newPatch.series) {
							originalFn(newPatch.series, false);
							patchWasApplied = true;
						}
					}
					else {
						patchWasApplied = card.applyCurrentChartPatch(newPatch, payloadInfo.currentRestoreOptions, host);
					}
					// only record the patch as applied when it actually succeeded on the current chart ref;
					// if the ref was null (e.g. *ngIf was false), the next pushCardPayloads must re-try
					if (patchWasApplied) {
						this.lastAppliedPatchOptions[key] = _.cloneDeep(newPatch);
					}
				}

				// horizontal category bars have no series in the patch — explicitly updateSeries
				// so ApexCharts always displays the current batch's data regardless of patch equality
				if (!newPatch.series && rawCurrent?.series) {
					const originalFn = this.originalUpdateSeriesFns.get(card.chartRefKey);
					if (originalFn) {
						const seriesSnapshot = rawCurrent.series;
						this.ngZone.runOutsideAngular(() => {
							setTimeout(() => {
								const chartRef = card.getCurrentChartRef(host);
								if (!chartRef) {
									return;
								}
								this.ngZone.run(() => {
									originalFn(seriesSnapshot, false);
								});
							}, 50);
						});
					}
				}

				// step 4 — smart update for right chart: skip if payload unchanged
				const newPayload = payloadInfo.payload;
				const payloadForCompare = {...newPayload, loading: undefined};
				const prevCompared = this.lastAppliedComparedOptions[key];

				if (!prevCompared || !_.isEqual(prevCompared, payloadForCompare)) {
					card.setPayload(newPayload);
					this.lastAppliedComparedOptions[key] = payloadForCompare;
				}

				card.injectTitles(newPayload.currentTitle, newPayload.comparedTitle);
				card.setLoading(!!newPayload.loading);
				payloadsBuilt++;
			});

			// after first batch with results, remove blocking overlay after 700ms
			if (!this.firstBatchShown && payloadsBuilt > 0 && this.comparisonRunOverlay) {
				this.firstBatchShown = true;
				setTimeout(() => {
					this.removeComparisonRunOverlay();
				}, 700);
			}
		}
		finally {
			this.dataProcessInProgress = false;
			this.drainPendingDataProcess();
		}
	}

	private drainPendingDataProcess() {
		if (this.pendingDataProcess) {
			this.pendingDataProcess = false;
			// CC: 100ms delay lets ApexCharts finish its render cycle from the previous push before the next one starts
			setTimeout(() => this.schedulePushCardPayloads(), 80);
		}
	}

	// CC: buildComparisonPayload produces two outputs per card key:
	// - currentPatchOptions: applied to the left (host) chart via updateOptions/updateSeries
	// - payload: the full right (ghost) chart options sent to the card's comparison renderer
	private buildComparisonPayload(key: string, rawCurrentInput: any, rawComparedInput: any, card: TrendsComparisonCardDirective) {
		const currentOptions = _.cloneDeep(rawCurrentInput);
		const rawComparedOptions = _.cloneDeep(rawComparedInput);

		const currentEmpty = !currentOptions?.series?.length ||
			(typeof currentOptions.series[0] !== 'number' && !currentOptions.series.some(s => s?.data?.length > 0));
		const comparedEmpty = !rawComparedOptions?.series?.length ||
			(typeof rawComparedOptions.series[0] !== 'number' && !rawComparedOptions.series.some(s => s?.data?.length > 0));

		// if compared data for this key hasn't been received yet (undefined), skip until it arrives or loading ends
		if (rawComparedInput === undefined && !this.allFinished) {
			return null;
		}

		// CC: titles computed before bothEmpty check so they are available for the both-empty payload
		const currentTitle = this.formatRangeTitle(this.comparisonParams?.currentRange, this.comparisonParams?.granularity);
		const comparedTitle = this.formatRangeTitle(this.comparisonParams?.comparedRange, this.comparisonParams?.granularity);
		// when current is empty, fall back to compared options for chart-type detection
		const isHorizontalCategory = this.isHorizontalCategoryBar(currentEmpty ? rawComparedOptions : currentOptions);
		const isPieDonut = currentOptions?.chart?.type === 'pie' || currentOptions?.chart?.type === 'donut';
		const isFlatSeries = typeof currentOptions?.series?.[0] === 'number';

		// -------------------------------------------------------------------------
		// CC: DATA ALIGNMENT
		// This block transforms normalizedCurrent and normalizedCompared so that both
		// charts are visually comparable: same categories, same scale, same chart type.
		// Add new visual alignment rules here. Each step is independent and labeled.
		// -------------------------------------------------------------------------

		// CC: DA-1 — Pie/donut label union: add labels that exist in compared but not in current (with 0 values)
		// so both sides always have the same label set and colors don't shift
		if (isPieDonut && !currentEmpty) {
			const currentLabels = currentOptions.labels || [];
			const comparedLabels = rawComparedOptions?.labels || [];
			const allLabels = Array.from(new Set([...currentLabels, ...comparedLabels]));
			if (allLabels.length > currentLabels.length) {
				const currentVals = currentOptions.series || [];
				const currentByLabel = {};
				currentLabels.forEach((l, i) => {
					currentByLabel[l] = currentVals[i] || 0;
				});
				currentOptions.labels = allLabels;
				currentOptions.series = allLabels.map(l => currentByLabel[l] || 0);
			}
		}

		// CC: DA-2 — Normalize + align: scale radialBar to 0-60 range; align compared categories/labels to match current order
		let normalizedCurrent = this.buildNormalizedCurrentOptions(currentOptions);
		let normalizedCompared = this.buildAlignedComparedOptions(normalizedCurrent, rawComparedOptions);

		// CC: DA-3 — Color map for flat series (pie/donut/radial): keep label→color stable across both sides
		if (isFlatSeries) {
			this.applyLabelColorMap(normalizedCurrent, normalizedCompared);
		}

		// CC: DA-4 — Date-based boundary enrichment: pad both sides with the period's start/end date labels
		// so gaps at the edges of the time range are visible rather than silently missing
		const isDateBased = this.isDateBasedCategories(normalizedCurrent?.xaxis?.categories || []) ||
			(currentEmpty && this.isDateBasedCategories(normalizedCompared?.xaxis?.categories || []));
		if (isDateBased) {
			const currentBoundaries = this.getPeriodBoundaryLabels(this.comparisonParams?.currentRange);
			const comparedBoundaries = this.getPeriodBoundaryLabels(this.comparisonParams?.comparedRange);
			const enrichedCurrent = this.enrichWithBoundaryDates(normalizedCurrent, currentBoundaries);
			const enrichedCompared = this.enrichWithBoundaryDates(normalizedCompared, comparedBoundaries);
			if (enrichedCurrent.changed) {
				normalizedCurrent.xaxis = {...(normalizedCurrent.xaxis || {}), categories: enrichedCurrent.categories};
				normalizedCurrent.series = enrichedCurrent.series;
			}
			if (enrichedCompared.changed) {
				normalizedCompared.xaxis = {...(normalizedCompared.xaxis || {}), categories: enrichedCompared.categories};
				normalizedCompared.series = enrichedCompared.series;
			}
		}

		// CC: DA-5 — Chart type: convert date-based bars to area (non-daily) so the two period lines can overlap meaningfully;
		// force bar for daily granularity and filter to the single selected day
		const shouldConvertToArea = isDateBased
			&& (normalizedCurrent?.chart?.type === 'bar' || normalizedCompared?.chart?.type === 'bar')
			&& this.comparisonParams?.granularity !== 'daily';
		if (shouldConvertToArea) {
			normalizedCurrent.chart = {...(normalizedCurrent.chart || {}), type: 'area'};
			normalizedCompared.chart = {...(normalizedCompared.chart || {}), type: 'area'};
		}
		if (!isHorizontalCategory && !isPieDonut && !isFlatSeries && this.comparisonParams?.granularity === 'daily') {
			normalizedCurrent.chart = {...(normalizedCurrent.chart || {}), type: 'bar'};
			normalizedCompared.chart = {...(normalizedCompared.chart || {}), type: 'bar'};
			if (isDateBased) {
				const currentDayLabel = this.getPeriodBoundaryLabels(this.comparisonParams?.currentRange)?.start;
				const comparedDayLabel = this.getPeriodBoundaryLabels(this.comparisonParams?.comparedRange)?.start;
				normalizedCurrent = this.filterToSingleDayLabel(normalizedCurrent, currentDayLabel);
				normalizedCompared = this.filterToSingleDayLabel(normalizedCompared, comparedDayLabel);
			}
		}

		// CC: DA-6 — Shared Y-axis: same max and tick count on both sides so bar heights are visually comparable
		const sharedYAxis = (!isPieDonut && !isHorizontalCategory && !isFlatSeries)
			? this.calcSharedYAxis(normalizedCurrent, normalizedCompared)
			: null;

		// -------------------------------------------------------------------------
		// CC: END DATA ALIGNMENT
		// -------------------------------------------------------------------------

		card.detectCurrentChartClass?.();
		const comparedRenderOptions = this.sanitizeChartOptions(normalizedCompared);
		comparedRenderOptions.title = {text: ''};
		comparedRenderOptions.chart = {...(comparedRenderOptions.chart || {}), height: '100%', width: '100%'};
		comparedRenderOptions.grid = {...(comparedRenderOptions.grid || {}), padding: {...(comparedRenderOptions.grid?.padding || {}), top: 25}};

		// rules 2/3 — vertical bar and pie/donut hide right chart independently when compared has no data;
		// radial (rule 1) always shows regardless;
		// horizontal (rule 4) keeps right chart alive ONLY when current has data (to show zero-filled comparison bars)
		const isRadialBar = currentOptions?.chart?.type === 'radialBar';
		if (comparedEmpty && this.allFinished && !isRadialBar && (!isHorizontalCategory || currentEmpty)) {
			comparedRenderOptions.series = [];
		}
		// pie/donut right chart must not show during loading (ghost placeholder values like [1,1,1] cause ApexCharts artifacts)
		// or when all aligned values are zero — both cases render as [1,1,1] visually
		if (isPieDonut && (!this.allFinished || (Array.isArray(comparedRenderOptions.series) &&
			comparedRenderOptions.series.every(v => (Number(v) || 0) === 0)))) {
			comparedRenderOptions.series = [];
		}

		if (sharedYAxis) {
			comparedRenderOptions.yaxis = sharedYAxis.forCompared;
		}
		if (isHorizontalCategory) {
			const yaxis = Array.isArray(comparedRenderOptions.yaxis) ? comparedRenderOptions.yaxis[0] : (comparedRenderOptions.yaxis || {});
			// for horizontal bars, left chart always shows (real data or zero-filled), so right chart labels are always hidden
			comparedRenderOptions.yaxis = {...yaxis, labels: {show: false}, axisBorder: {show: false}, axisTicks: {show: false}};
			// CC: sync right chart barHeight to left chart's when left has data, so bars appear at the same height
			if (!currentEmpty) {
				const leftBarHeight = normalizedCurrent?.plotOptions?.bar?.barHeight;
				if (leftBarHeight) {
					comparedRenderOptions.plotOptions = {
						...(comparedRenderOptions.plotOptions || {}),
						bar: {
							...(comparedRenderOptions.plotOptions?.bar || {}),
							barHeight: leftBarHeight
						}
					};
				}
			}
		}

		// when current is empty on a horizontal bar, use compared categories as delta base (all zeros vs compared)
		const horizontalCurrentForDeltas = (isHorizontalCategory && currentEmpty && !comparedEmpty)
			? {xaxis: {categories: normalizedCompared?.xaxis?.categories || []}, series: []}
			: normalizedCurrent;
		const defaultPolarity = currentOptions?.defaultPolarity ?? (isHorizontalCategory ? 'negative' : undefined);
		const deltas = isHorizontalCategory
			? this.comparisonService.calcCategoryDeltas(horizontalCurrentForDeltas, normalizedCompared, defaultPolarity)
			: this.comparisonService.calcSeriesDeltas(normalizedCurrent, normalizedCompared, defaultPolarity);

		// don't force-patch series/labels/colors onto the left chart when current has no data
		const patchRestore = this.buildCurrentPatchAndRestore(normalizedCurrent, rawCurrentInput, currentTitle, sharedYAxis, shouldConvertToArea, (isPieDonut || isFlatSeries) && !currentEmpty);

		// req 1 — keep series in sync with enriched/filtered xaxis categories for date-based bar/area charts
		if (isDateBased && !isPieDonut && !isFlatSeries) {
			patchRestore.patch.series = normalizedCurrent.series;
			patchRestore.restore.series = rawCurrentInput?.series;
		}

		// rule 1/2/3 — when current has no data, force an empty series (host placeholder handles the "no data" message)
		if (currentEmpty) {
			patchRestore.patch.series = [];
			patchRestore.restore.series = rawCurrentInput?.series;
		}

		if (isHorizontalCategory) {
			const sharedXMax = this.calcSharedXAxisMax(normalizedCurrent, normalizedCompared);
			patchRestore.patch.xaxis = {...(patchRestore.patch.xaxis || {}), max: sharedXMax};
			patchRestore.restore.xaxis = patchRestore.restore.xaxis || {};
			comparedRenderOptions.xaxis = {...(comparedRenderOptions.xaxis || {}), max: sharedXMax};
			// CC: match the right chart's grid.padding.top on the left chart so both graphical areas share the same y-offset and bar rows align
			patchRestore.patch.grid = {...(normalizedCurrent.grid || {}), padding: {...(normalizedCurrent.grid?.padding || {}), top: 25}};
			patchRestore.restore.grid = rawCurrentInput?.grid || {};
		}

		// horizontal bar special case — when current is empty but compared has data,
		// show left chart with compared categories and zero values instead of the noData placeholder
		if (currentEmpty && isHorizontalCategory && !comparedEmpty) {
			const zeroSeries = (normalizedCompared.series || []).map(s => ({
				...s,
				data: Array.isArray(s.data) ? s.data.map(() => 0) : s.data
			}));
			patchRestore.patch.series = zeroSeries;
			patchRestore.patch.xaxis = {...(patchRestore.patch.xaxis || {}), categories: normalizedCompared?.xaxis?.categories || []};

			// CC: mutate the host's chart options so the *ngIf guards (xaxis.categories.length) evaluate to true
			// and the zero-filled left chart can render. prepareData naturally restores everything on the next call.
			const host = this.appTrendsComparison?.host;
			if (host) {
				if (host[key]) {
					host[key].xaxis = {...(host[key].xaxis || {}), categories: normalizedCompared?.xaxis?.categories || []};
					host[key].series = zeroSeries;
				}
			}
		}

		return {
			currentPatchOptions: patchRestore.patch,
			currentRestoreOptions: patchRestore.restore,
			payload: {
				comparisonKey: key,
				currentTitle,
				comparedTitle,
				comparedOptions: comparedRenderOptions,
				deltas,
				isHorizontalCategory,
				loading: false,
				chartClass: card.chartClass
			}
		};
	}

	// CC: patch = options applied to the left (host) chart during comparison; restore = original values to put back when comparison ends.
	// Both are minimal — only the properties that need to change are included.
	private buildCurrentPatchAndRestore(normalizedCurrent: any, originalCurrent: any, currentTitle: string, sharedYAxis: any, shouldConvertToArea: boolean, isFlatOrPie: boolean) {
		const patch: any = {
			chart: {...(normalizedCurrent.chart || {}), height: normalizedCurrent?.chart?.height || '100%', width: '100%'},
		};
		const restore: any = {
			chart: originalCurrent?.chart || {},
		};

		if (shouldConvertToArea) {
			patch.chart = {...patch.chart, type: 'area'};
			restore.chart = {...(restore.chart || {}), type: originalCurrent?.chart?.type || 'bar'};
		}

		if (sharedYAxis) {
			patch.yaxis = sharedYAxis.forCurrent;
			restore.yaxis = originalCurrent?.yaxis || {};
		}

		if (normalizedCurrent?.xaxis) {
			const existingLabels = normalizedCurrent?.xaxis?.labels || {};
			patch.xaxis = {...normalizedCurrent.xaxis, labels: {...existingLabels, rotate: 0, hideOverlappingLabels: true}};
			restore.xaxis = originalCurrent?.xaxis || {};
		}

		// Flat series charts (donut / pie) depend on label-index color matching — re-apply after every batch.
		// radialBar is excluded: patching series/colors via updateOptions corrupts the ApexCharts internal state,
		// breaking both the original chart and subsequent updates. Its arcs are driven by Angular bindings instead.
		if (isFlatOrPie && normalizedCurrent?.chart?.type !== 'radialBar') {
			patch.series = normalizedCurrent.series;
			patch.labels = normalizedCurrent.labels || [];
			patch.colors = normalizedCurrent.colors || [];
			patch.fill = normalizedCurrent.fill || originalCurrent?.fill;
			patch.legend = normalizedCurrent.legend || originalCurrent?.legend;
			restore.series = originalCurrent?.series;
			restore.labels = originalCurrent?.labels;
			restore.colors = originalCurrent?.colors;
			restore.fill = originalCurrent?.fill;
			restore.legend = originalCurrent?.legend;
		}

		return {patch, restore};
	}

	private buildNormalizedCurrentOptions(currentOptions: any) {
		const result = _.cloneDeep(currentOptions || {});
		const isFlatSeries = typeof result?.series?.[0] === 'number';
		if (!isFlatSeries) {
			return result;
		}

		const labels = result.labels || [];
		// CC: use rawValues (raw counts) when present — series may already be scaled (e.g. radialBar stores scaled 0-60)
		const values = result.rawValues || result.series || [];

		// Keep a stable label -> color map for this chart key during comparison.
		// New labels receive the color provided by the current options at their first appearance;
		// existing labels keep their established color even if the component reorders labels later.
		result.fill = {...(result.fill || {}), colors: result.colors};
		result.legend = {
			...(result.legend || {}),
			markers: {
				...(result.legend?.markers || {}),
				fillColors: result.colors
			}
		};

		if (result.chart?.type === 'radialBar') {
			let highest = Math.max(...values.map(v => Number(v) || 0));
			if (highest < 15) {
				highest = 15;
			}
			result.rawValues = values;
			result.series = values.map(value => (Number(value) || 0) * 60 / highest);
			const pctSuffix = result._percentageValues ? '%' : '';
			result.legend = {
				...(result.legend || {}),
				formatter: (seriesName, opts) => seriesName + ':  ' + (values[opts.seriesIndex] || 0) + pctSuffix
			};
		}

		return result;
	}

	private buildAlignedComparedOptions(currentOptions, comparedOptions) {
		if (!currentOptions?.series?.length ||
			(typeof currentOptions.series[0] !== 'number' && !currentOptions.series.some(s => s?.data?.length > 0))) {
			return comparedOptions ? _.cloneDeep(comparedOptions) : {};
		}

		const currentSeries = currentOptions.series;
		if (typeof currentSeries[0] === 'number') {
			const result = _.cloneDeep(comparedOptions || currentOptions);
			const currentLabels = currentOptions.labels || [];
			const comparedLabels = comparedOptions?.labels || [];
			const comparedIndexByLabel = {};

			comparedLabels.forEach((label, index) => {
				comparedIndexByLabel[label] = index;
			});

			// CC: use rawValues (raw counts) when present — radialBar stores scaled series, rawValues holds the real counts
			const comparedRealValues = comparedOptions?.rawValues || comparedOptions?.series || [];
			const alignedRealValues = currentLabels.map(label => {
				const comparedIndex = comparedIndexByLabel[label];
				return comparedIndex !== undefined ? (comparedRealValues[comparedIndex] || 0) : 0;
			});

			result.labels = [...currentLabels];

			if (currentOptions?.chart?.type === 'radialBar' || result?.chart?.type === 'radialBar') {
				let highest = Math.max(...alignedRealValues.map(v => Number(v) || 0));
				if (highest < 15) {
					highest = 15;
				}
				result.rawValues = alignedRealValues;
				result.series = alignedRealValues.map(value => (Number(value) || 0) * 60 / highest);
				const pctSuffix = currentOptions?._percentageValues ? '%' : '';
				result.legend = {
					...(result.legend || {}),
					formatter: (seriesName, opts) => seriesName + ':  ' + (alignedRealValues[opts.seriesIndex] || 0) + pctSuffix
				};
			}
			else {
				result.series = [...alignedRealValues];
			}

			return result;
		}

		const currentCategories = currentOptions.xaxis?.categories || [];
		const result = _.cloneDeep(comparedOptions || currentOptions);

		// date-based x-axis — keep compared period's own dates; boundary enrichment happens later
		if (this.isDateBasedCategories(currentCategories)) {
			return result;
		}

		// category-based (emails, domains) — align to current chart's categories, filling 0 for missing
		const comparedCategories = comparedOptions?.xaxis?.categories || [];
		const comparedSeriesArr = comparedOptions?.series || [];
		const catIndexMap = {};
		comparedCategories.forEach((cat, i) => {
			catIndexMap[cat] = i;
		});

		result.series = currentSeries.map((currentSerie, si) => {
			const matchedCompared = comparedSeriesArr.find(s => s.name === currentSerie.name) || comparedSeriesArr[si];
			const matchedData = matchedCompared?.data || [];
			return {
				..._.cloneDeep(currentSerie),
				data: currentCategories.map(cat => {
					const ci = catIndexMap[cat];
					return ci !== undefined ? (matchedData[ci] || 0) : 0;
				})
			};
		});

		result.xaxis = {...(result.xaxis || {}), categories: [...currentCategories]};
		if (currentOptions?.colors) {
			result.colors = [...currentOptions.colors];
		}
		// CC: inherit chart type and plotOptions from current when compared has none (e.g. fallback {series:[]} for no-data ghost)
		if (!result.chart?.type && currentOptions?.chart?.type) {
			result.chart = {...(result.chart || {}), type: currentOptions.chart.type};
		}
		if (!result.plotOptions && currentOptions?.plotOptions) {
			result.plotOptions = _.cloneDeep(currentOptions.plotOptions);
		}
		return result;
	}

	private applyLabelColorMap(currentOptions: any, targetOptions: any) {
		const labels = currentOptions?.labels || [];
		const colors = currentOptions?.colors || [];
		if (!labels.length || !colors.length || !targetOptions) {
			return;
		}

		const colorByLabel = {};
		labels.forEach((label, index) => {
			colorByLabel[label] = colors[index];
		});

		const targetLabels = targetOptions.labels || labels;
		const alignedColors = targetLabels.map((label, index) => colorByLabel[label] || colors[index]);

		targetOptions.colors = alignedColors;
		targetOptions.fill = {...(targetOptions.fill || {}), colors: alignedColors};
		targetOptions.legend = {
			...(targetOptions.legend || {}),
			markers: {
				...(targetOptions.legend?.markers || {}),
				fillColors: alignedColors
			}
		};
	}

	private calcSharedYAxis(currentOptions, comparedOptions) {
		const getSeriesMax = (opts) => {
			const s = opts?.series;
			if (!s?.length) {
				return 0;
			}
			if (typeof s[0] === 'number') {
				return Math.max(0, ...s.filter(v => typeof v === 'number'));
			}
			return Math.max(0, ...s.flatMap(item => (item?.data || []).map(v => typeof v === 'number' ? v : (v?.y ?? 0))));
		};
		const currentMax = getSeriesMax(currentOptions);
		const comparedMax = getSeriesMax(comparedOptions);
		const rawMax = Math.max(currentMax, comparedMax);
		const protoYAxis = currentMax >= comparedMax ? currentOptions?.yaxis : comparedOptions?.yaxis;
		const protoYAxisObj = Array.isArray(protoYAxis) ? protoYAxis[0] : (protoYAxis || {});
		const tickAmount = protoYAxisObj.tickAmount || 5;
		const step = Math.max(1, Math.ceil(rawMax * 1.05 / tickAmount / 5) * 5);
		const sharedMax = step * tickAmount;
		return {
			forCurrent: {...protoYAxisObj, min: 0, max: sharedMax, tickAmount},
			forCompared: {min: 0, max: sharedMax, tickAmount, labels: {show: false}, axisBorder: {show: false}, axisTicks: {show: false}}
		};
	}

	private calcSharedXAxisMax(currentOptions, comparedOptions) {
		const isStacked = currentOptions?.chart?.stacked === true;
		const getMax = (opts) => {
			const s = opts?.series;
			if (!s?.length) {
				return 0;
			}
			if (isStacked) {
				const dataLength = Math.max(...s.map(item => (item?.data || []).length));
				let maxSum = 0;
				for (let i = 0; i < dataLength; i++) {
					const rowSum = s.reduce((acc, item) => acc + (Number(item?.data?.[i]) || 0), 0);
					if (rowSum > maxSum) {
						maxSum = rowSum;
					}
				}
				return maxSum;
			}
			return Math.max(0, ...s.flatMap(item => (item?.data || []).map(v => typeof v === 'number' ? v : (v?.y ?? 0))));
		};
		const rawMax = Math.max(getMax(currentOptions), getMax(comparedOptions));
		return Math.ceil(rawMax / 5) * 5 || 5;
	}

	private isHorizontalCategoryBar(options) {
		if (options?.plotOptions?.bar?.horizontal !== true) {
			return false;
		}
		const categories = options?.xaxis?.categories || [];
		return !this.isDateBasedCategories(categories);
	}

	private isDateBasedCategories(categories) {
		if (!categories?.length) {
			return false;
		}
		const first = categories[0];
		return typeof first === 'string' && /^[A-Za-z]{3} \d{1,2}\/\d{1,2}$/.test(first);
	}

	private getPeriodBoundaryLabels(range) {
		if (!range?.start || !range?.end) {
			return {start: null, end: null};
		}
		return {
			start: this.formatDateLabel(new Date(range.start)),
			end: this.formatDateLabel(new Date(range.end))
		};
	}

	private enrichWithBoundaryDates(options, boundaries) {
		const cats = [...(options?.xaxis?.categories || [])];
		const series = (options?.series || []).map(s => ({...s, data: [...(s.data || [])]}));
		let changed = false;

		if (boundaries.start && !cats.includes(boundaries.start)) {
			cats.unshift(boundaries.start);
			series.forEach(s => s.data.unshift(0));
			changed = true;
		}
		if (boundaries.end && !cats.includes(boundaries.end)) {
			cats.push(boundaries.end);
			series.forEach(s => s.data.push(0));
			changed = true;
		}
		return {categories: cats, series, changed};
	}

	// req 2 — keep only the single relevant day label in the chart for daily granularity
	private filterToSingleDayLabel(options, dayLabel) {
		if (!dayLabel) {
			return options;
		}
		const cats = options?.xaxis?.categories || [];
		const idx = cats.indexOf(dayLabel);
		const result = _.cloneDeep(options);
		result.xaxis = {...(result.xaxis || {}), categories: [dayLabel]};
		if (idx === -1) {
			result.series = (result.series || []).map(s => ({...s, data: [0]}));
		}
		else {
			result.series = (result.series || []).map(s => ({...s, data: [s.data?.[idx] ?? 0]}));
		}
		return result;
	}

	private formatDateLabel(date) {
		const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
		return `${dayNames[date.getDay()]} ${date.getMonth() + 1}/${date.getDate()}`;
	}

	private sanitizeChartOptions(options) {
		const sanitized = _.cloneDeep(options || {});
		sanitized.chart = sanitized.chart || {};
		delete sanitized.chart.events;
		sanitized.chart.toolbar = {show: false};
		sanitized.chart.animations = {
			enabled: false,
			speed: 0,
			animateGradually: {enabled: false},
			dynamicAnimation: {enabled: false}
		};

		const chartType = sanitized.chart?.type;
		if (chartType !== 'pie' && chartType !== 'donut') {
			sanitized.xaxis = sanitized.xaxis || {};
			sanitized.xaxis.labels = {...(sanitized.xaxis.labels || {}), rotate: 0, hideOverlappingLabels: true};
		}

		if (chartType === 'pie' || chartType === 'donut') {
			const allZero = !sanitized.series?.length || (sanitized.series as any[]).every(v => !v);
			if (allZero) {
				const n = sanitized.labels?.length || 1;
				sanitized.series = Array(n).fill(1);
				sanitized.dataLabels = {enabled: false};
				if (sanitized.plotOptions?.pie?.donut?.labels) {
					sanitized.plotOptions.pie.donut.labels = {show: false};
				}
			}
		}
		return sanitized;
	}

	private formatRangeTitle(range, granularity) {
		if (!range?.start) {
			return '';
		}
		const start = new Date(range.start);
		if (granularity === 'monthly') {
			return start.toLocaleString('default', {month: 'short', year: 'numeric'});
		}
		if (granularity === 'daily') {
			return start.toLocaleDateString('default', {month: 'short', day: 'numeric', year: 'numeric'});
		}
		const end = new Date(range.end);
		const fmt = (d) => d.toLocaleDateString('default', {month: 'short', day: 'numeric'});
		return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
	}

	private teardown(restoreDefault = true) {
		this.dataProcessInProgress = false;
		this.pendingDataProcess = false;
		const host = this.appTrendsComparison?.host;
		this.restoreGetTrendsDataInProcess(host);
		this.restoreUpdateSeriesFns(host);
		this.allFinished = false;
		this.pushToken++;
		this.setCardsLoading(false);
		this.setCardsActive(false);
		this.renderer.removeClass(this.el.nativeElement, 'comparison-mode');
		if (this.restoreHostHooks) {
			this.restoreHostHooks();
		}
		this.removeComparisonRunOverlay();
		this.destroyGhost();
		this.pendingLoadingReset = false;
		if (restoreDefault) {
			this.comparisonService.comparisonChartOptions = {};
		}
		this.rawCurrentOptions = {};
		this.rawComparedOptions = {};
		this.lastAppliedPatchOptions = {};
		this.lastAppliedComparedOptions = {};
		this.pendingSeriesUpdates = {};
	}

	private destroyGhost() {
		if (this.ghostRef) {
			try {
				this.ghostRef.destroy();
			}
			catch (e) {}
			this.ghostRef = null;
		}
		this.comparisonService.ghostInstance = null;
	}

	private interceptGetTrendsDataInProcess(host) {
		// CC: force getTrendsDataInProcess to always return false during comparison so that
		// *ngIf conditions never destroy chart refs, preventing flicker and updateSeries interception loss
		this.getTrendsDataInProcessDescriptor = Object.getOwnPropertyDescriptor(host, 'getTrendsDataInProcess') || null;
		Object.defineProperty(host, 'getTrendsDataInProcess', {
			get: () => false,
			set: () => {},
			configurable: true
		});
	}

	private restoreGetTrendsDataInProcess(host) {
		if (!host) {
			return;
		}
		const desc = Object.getOwnPropertyDescriptor(host, 'getTrendsDataInProcess');
		if (desc?.get) {
			delete host.getTrendsDataInProcess;
		}
		this.getTrendsDataInProcessDescriptor = null;
	}

	private interceptUpdateSeriesForCard(card: TrendsComparisonCardDirective, host) {
		if (!card.chartRefKey) {
			return;
		}
		const chartRef = card.getCurrentChartRef(host);
		if (!chartRef?.updateSeries) {
			return;
		}

		const alreadyTracked = this.originalUpdateSeriesFns.has(card.chartRefKey);
		// if already tracked and current ref's updateSeries is still our intercept, skip
		if (alreadyTracked && (chartRef.updateSeries as any).__intercepted) {
			return;
		}

		const key = card.comparisonKey;
		const originalFn = chartRef.updateSeries.bind(chartRef);
		this.originalUpdateSeriesFns.set(card.chartRefKey, originalFn);
		// CC: intercept updateSeries so batch-driven series updates are captured into pendingSeriesUpdates
		// instead of being applied directly — they're merged into rawCurrentOptions on the next prepareData call
		const interceptFn: any = (newSeries, animate?) => {
			this.pendingSeriesUpdates[key] = newSeries;
		};
		interceptFn.__intercepted = true;
		chartRef.updateSeries = interceptFn;

		// chart ref was recreated after *ngIf destroy — force patch re-apply on next push
		if (alreadyTracked) {
			delete this.lastAppliedPatchOptions[key];
		}
	}

	private restoreUpdateSeriesFns(host) {
		if (!host) {
			return;
		}
		this.originalUpdateSeriesFns.forEach((originalFn, chartRefKey) => {
			const chartRef = host[chartRefKey];
			if (chartRef) {
				chartRef.updateSeries = originalFn;
			}
		});
		this.originalUpdateSeriesFns.clear();
	}
}
